import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getModelCapability,
  getUserSelectableModels,
  MODEL_TIERS,
  type ModelTier,
} from '@inference.engine/model-registry/web';
import { InspectorField } from '@inference.engine/agentic-ui/components';
import { Checkbox, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import type { AgentCapabilities } from '../model/types';

/** The tiers that have a model people can pick today, cheapest first, with those models' names. */
const TIERS = MODEL_TIERS.flatMap((tier) => {
  const models = getUserSelectableModels()
    .filter((model) => model.tier === tier)
    .map((model) => model.label);
  return models.length > 0 ? [{ tier, models }] : [];
});
const ALL_TIERS = TIERS.map((entry) => entry.tier);

/**
 * The tiers an agent offers when it never chose: every tier, or, for models picked one by one
 * before tiers, the tiers those models sit in, so the checkboxes show what the chat offers.
 */
function tiersOf(picked: AgentCapabilities['supportedModels']): ModelTier[] {
  if (!picked) return ALL_TIERS;
  const held = new Set(picked.map((model) => getModelCapability(model.key)?.tier));
  return ALL_TIERS.filter((tier) => held.has(tier));
}

// eslint-disable-next-line i18next/no-literal-string -- option values, labelled through t()
const MODES = ['fixed', 'choose'] as const;
type Mode = (typeof MODES)[number];

export interface ModelChoiceFieldProps {
  metadata: Record<string, unknown>;
  /** The inspector's own model select, shown for a fixed model. */
  select: ReactNode;
  onChange: (patch: { model?: string | undefined; metadata?: Record<string, unknown> }) => void;
}

/**
 * Whether the agent answers with one model, or people pick one in the chat from the tiers it allows.
 * Never both: with people choosing, the agent's own model is cleared rather than left to compete
 * with the picker over which one a turn runs on.
 */
export function ModelChoiceField({ metadata, select, onChange }: ModelChoiceFieldProps) {
  const { t } = useTranslation('agentBuilder');
  const capabilities = (metadata.capabilities ?? {}) as AgentCapabilities;
  const tiers = capabilities.modelTiers ?? tiersOf(capabilities.supportedModels);
  // Models picked one by one, before tiers, are people choosing too, whichever tiers they sit in.
  const pickedOneByOne = !capabilities.modelTiers && !!capabilities.supportedModels?.length;
  const mode: Mode = tiers.length > 0 || pickedOneByOne ? 'choose' : 'fixed';

  const withTiers = (modelTiers: ModelTier[]) => ({
    ...metadata,
    capabilities: Object.fromEntries(
      Object.entries({ ...capabilities, modelTiers, supportedModels: undefined }).filter(
        ([, value]) => value !== undefined,
      ),
    ),
  });

  return (
    <>
      <InspectorField label={t('details.modelMode.label')} as="div">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={mode}
          className="w-full"
          aria-label={t('details.modelMode.label')}
        >
          {MODES.map((each) => (
            <ToggleGroupItem
              key={each}
              value={each}
              className="flex-1"
              // Per item: Radix reports an empty value when the pressed item is pressed again.
              onClick={() => {
                if (each === mode) return;
                onChange(
                  each === 'fixed'
                    ? { metadata: withTiers([]) }
                    : { model: undefined, metadata: withTiers(ALL_TIERS) },
                );
              }}
            >
              {t(`details.modelMode.${each}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </InspectorField>
      {mode === 'fixed' ? (
        <InspectorField label={t('details.runsOn')} hint={t('details.runsOnHint')}>
          {select}
        </InspectorField>
      ) : (
        <InspectorField label={t('details.models')} hint={t('details.modelsHint')} as="div">
          <div className="flex flex-col gap-2">
            {TIERS.map(({ tier, models }) => {
              const id = `model-tier-${tier}`;
              const ticked = tiers.includes(tier);
              return (
                <div key={tier} className="flex items-start gap-2 text-sm">
                  <Checkbox
                    id={id}
                    className="mt-0.5"
                    aria-describedby={`${id}-models`}
                    checked={ticked}
                    // The last tier stays: none ticked is a fixed model, which the switch above sets.
                    disabled={ticked && tiers.length === 1}
                    onCheckedChange={(on) => {
                      onChange({
                        metadata: withTiers(
                          ALL_TIERS.filter((each) =>
                            each === tier ? on === true : tiers.includes(each),
                          ),
                        ),
                      });
                    }}
                  />
                  <span className="flex flex-col">
                    <label htmlFor={id} className="text-foreground">
                      {t(`details.tier.${tier}`)}
                    </label>
                    <span id={`${id}-models`} className="text-xs text-muted-foreground">
                      {models.join(', ')}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </InspectorField>
      )}
    </>
  );
}
